import { useEffect } from 'react'

export default function PaymentLoader({
  visible,
  status = 'processing', // 'processing' | 'verifying' | 'success' | 'failed'
  message = ''
}) {
  // Prevent body scroll while overlay is showing
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [visible])

  if (!visible) return null

  const states = {
    processing: {
      icon: (
        <div style={{
          width: 64,
          height: 64,
          border: '5px solid rgba(166,124,82,0.2)',
          borderTop: '5px solid #A67C52',
          borderRadius: '50%',
          animation: 'spin 0.9s linear infinite',
          margin: '0 auto 20px'
        }} />
      ),
      title: 'Processing Payment',
      subtitle: message || 'Please wait — do not close or refresh this page',
      color: '#A67C52'
    },
    verifying: {
      icon: (
        <div style={{
          width: 64,
          height: 64,
          border: '5px solid rgba(59,130,246,0.2)',
          borderTop: '5px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 0.9s linear infinite',
          margin: '0 auto 20px'
        }} />
      ),
      title: 'Verifying Payment',
      subtitle: message || 'Almost there — confirming your payment securely',
      color: '#3b82f6'
    },
    success: {
      icon: (
        <div style={{
          width: 72,
          height: 72,
          background: '#f0fdf4',
          border: '4px solid #22c55e',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
          animation: 'popIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}>
          <svg width="36" height="36" viewBox="0 0 24 24"
               fill="none" stroke="#22c55e" strokeWidth="3"
               strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
      ),
      title: 'Payment Successful!',
      subtitle: message || 'Your premium access is now active. Redirecting...',
      color: '#22c55e'
    },
    failed: {
      icon: (
        <div style={{
          width: 72,
          height: 72,
          background: '#fef2f2',
          border: '4px solid #ef4444',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
          animation: 'popIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24"
               fill="none" stroke="#ef4444" strokeWidth="3"
               strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </div>
      ),
      title: 'Payment Failed',
      subtitle: message || 'Something went wrong. Please try again.',
      color: '#ef4444'
    }
  }

  const current = states[status] || states.processing

  return (
    <>
      {/* Backdrop */}
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        zIndex: 9998,
        animation: 'fadeIn 0.2s ease',
        pointerEvents: 'all',
      }} />

      {/* Card */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 9999,
        background: 'white',
        borderRadius: 20,
        padding: '48px 40px 40px',
        width: '90%',
        maxWidth: 380,
        textAlign: 'center',
        boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
        animation: 'slideUp 0.3s ease',
        pointerEvents: 'all',
      }}>
        {current.icon}

        <h2 style={{
          margin: '0 0 10px',
          fontSize: 22,
          fontWeight: 700,
          color: '#1a1a1a',
        }}>
          {current.title}
        </h2>

        <p style={{
          margin: '0 0 24px',
          fontSize: 14,
          color: '#6b7280',
          lineHeight: 1.6,
        }}>
          {current.subtitle}
        </p>

        {(status === 'processing' || status === 'verifying') && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 6,
          }}>
            {[0, 1, 2].map(i => (
              <div
                key={i}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: current.color,
                  animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                  opacity: 0.3,
                }}
              />
            ))}
          </div>
        )}

        {status === 'failed' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: '#fef2f2',
                color: '#ef4444',
                border: '1px solid #fecaca',
                borderRadius: 10,
                padding: '10px 24px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                width: '100%',
              }}
            >
              🔄 Try Again
            </button>
            <a
              href="mailto:support@thachari.com?subject=Payment Failed"
              style={{
                display: 'block',
                textAlign: 'center',
                padding: '10px 24px',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                color: '#6b7280',
                fontSize: 13,
                textDecoration: 'none',
              }}
            >
              ✉️ Contact Support
            </a>
          </div>
        )}

        {(status === 'processing' || status === 'verifying') && (
          <p style={{
            margin: '20px 0 0',
            fontSize: 11,
            color: '#9ca3af',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24"
                 fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            Secured by Razorpay — 256-bit encryption
          </p>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes popIn {
          from { transform: scale(0.5); opacity: 0; }
          to   { transform: scale(1);   opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </>
  )
}
